import type { RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';

import type { ChatItem } from './services/types';

interface UseScrollToLatestMessageProps {
	chatContainerRef: RefObject<HTMLDivElement>;
	items: ChatItem[];
	mostRecentUserItemRef: RefObject<HTMLDivElement | null>;
	thinking: boolean;
}

export const useScrollToLatestMessage = ({
	chatContainerRef,
	items,
	mostRecentUserItemRef,
	thinking,
}: UseScrollToLatestMessageProps) => {
	const prevItemsLengthRef = useRef(0);
	const prevThinkingRef = useRef(false);
	const [isScrollReady, setIsScrollReady] = useState(true);

	useEffect(() => {
		const itemsGrew = items.length > prevItemsLengthRef.current;
		const startedThinking = thinking && !prevThinkingRef.current;
		const isChatLoad = prevItemsLengthRef.current === 0 && itemsGrew;

		prevItemsLengthRef.current = items.length;
		prevThinkingRef.current = thinking;

		const shouldScroll = itemsGrew || startedThinking;
		const scrollBehavior: ScrollBehavior = isChatLoad ? 'auto' : 'smooth';

		if (!shouldScroll) return undefined;

		const scrollToMostRecentUserMessage = () => {
			const userMessage = mostRecentUserItemRef.current;
			const chatContainer = chatContainerRef.current;
			if (!userMessage || !chatContainer) return;

			const containerOverflow = window.getComputedStyle(chatContainer).overflowY;
			const isContainerScrollable = containerOverflow === 'auto' || containerOverflow === 'scroll';

			if (isContainerScrollable) {
				const targetPosition = userMessage.offsetTop - chatContainer.offsetTop;
				chatContainer.scrollTo({ behavior: scrollBehavior, top: targetPosition });
			} else {
				const { bottom, top } = userMessage.getBoundingClientRect();
				const chatBottom = chatContainer.getBoundingClientRect().bottom;
				const isContentOutsideViewport = top < 0 || bottom > window.innerHeight || chatBottom > window.innerHeight;
				if (isContentOutsideViewport) {
					userMessage.scrollIntoView({ behavior: 'smooth', block: 'start' });
				}
			}
		};

		if (isChatLoad) {
			setIsScrollReady(false);
			const frame = requestAnimationFrame(() => {
				scrollToMostRecentUserMessage();
				setIsScrollReady(true);
			});
			return () => cancelAnimationFrame(frame);
		}

		const frame = requestAnimationFrame(scrollToMostRecentUserMessage);
		return () => cancelAnimationFrame(frame);
	}, [chatContainerRef, items, mostRecentUserItemRef, thinking]);

	return { isScrollReady };
};
